import { Form, Input, message, Radio, Spin, Typography } from 'antd';
import PageMeta from 'components/PageMeta/PageMeta';
import { Banner } from 'components/ui/Banner';
import { getPhoneNumberValidator, isPhoneNumberValid, validateEmail } from 'utils';
import { useCallback, useEffect, useState } from 'react';
import { familyService } from 'services';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import PhoneNumber from 'components/PhoneNumber';
import { selectors } from 'features/auth';
import { useSelector } from 'react-redux';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { getApplicantFirstName } from '../../utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyApplicationContactInfo({
  application,
  setApplication,
  household,
  setStep,
  loading,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);
  const currentUser = useSelector(selectors.selectUser);

  const [submitting, setSubmitting] = useState(false);

  // Application
  const isCompletedBySponsor = application?.completedBySponsor === true;

  // Form state
  const [form] = Form.useForm();
  const emailFormValue = Form.useWatch('email', form);
  const phoneFormValue = Form.useWatch('phone', form);

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.CONTACT_INFORMATION, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const _newSponsorData = await familyService.addUpdateAdult(household?.id, application?.applicant?.id ?? null, {
          ...application?.applicant,
          email: values?.email,
          phone: values?.phone,
          phoneType: values?.phoneType,
        });

        const _newApplication = await familyService.saveApplication({
          ...application,
          applicant: {
            ...application?.applicant,
            ..._newSponsorData,
          },
        });

        setApplication(_newApplication);
        _goToNextStep(_newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic.noticeError(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  // Set initial form values
  useEffect(() => {
    if (application?.applicant) {
      form.setFieldsValue({
        email: application?.applicant?.email || (isCompletedBySponsor ? currentUser?.email : null),
        phone: application?.applicant?.phone,
        phoneType: application?.applicant?.phoneType,
      });
    }
  }, [application, currentUser, form, isCompletedBySponsor]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <>
      <PageMeta title="Contact Information" />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7">
          <Banner
            question={
              isCompletedBySponsor
                ? 'How would you like to be contacted?'
                : `How should we contact ${getApplicantFirstName(application?.applicant)}?`
            }
            guidance={isCompletedBySponsor ? null : <p>We'll ask for your contact info later</p>}
            className="mb-12"
            data-testid="contact-info-banner"
          />

          {/*
           * Email input
           */}
          <Form.Item
            label={
              <div className="flex items-center gap-2">
                {isCompletedBySponsor ? 'Your email address' : 'Their email address'}
              </div>
            }
            name="email"
            rules={[
              {
                required: true,
                message: `Please enter an email address`,
              },
              {
                type: 'email',
                message: `Please enter a valid email address`,
              },
            ]}
            className="mb-0"
          >
            <Input data-testid="email-input" />
          </Form.Item>

          <Typography.Text type="secondary" className="block mt-2 max-w-[800px]" data-testid="email-guidance">
            Personal email works best - .mil addresses may block our attachments
          </Typography.Text>

          {emailFormValue && validateEmail(emailFormValue) && (
            <>
              {/*
               * Phone input
               */}
              <Form.Item
                label={isCompletedBySponsor ? 'Your phone number' : 'Their phone number'}
                name="phone"
                rules={[
                  {
                    required: true,
                    message: `Please enter a phone number`,
                  },
                  getPhoneNumberValidator('Invalid phone number'),
                ]}
                validateFirst
                className="mt-8 mb-0"
              >
                <PhoneNumber data-testid="phone-input" />
              </Form.Item>

              <Typography.Text type="secondary" className="block mt-2 max-w-[800px]" data-testid="phone-guidance">
                Cell phone preferred
              </Typography.Text>

              {phoneFormValue && isPhoneNumberValid(phoneFormValue) && (
                <Form.Item
                  name="phoneType"
                  label={isCompletedBySponsor ? 'This phone is my' : 'This phone is their'}
                  rules={[
                    {
                      required: true,
                      message: 'Please select a phone type',
                    },
                  ]}
                  className="mt-8 mb-0"
                >
                  <Radio.Group
                    block
                    optionType="button"
                    buttonStyle="solid"
                    className="flex flex-wrap"
                    data-testid="phone-type-group"
                  >
                    {phoneTypeOptions.map((option) => (
                      <Radio.Button
                        key={option.value}
                        value={option.value}
                        className="flex-[unset] min-w-[90px]"
                        data-testid={`phone-type-${option.value.toLowerCase()}`}
                      >
                        {option.title}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </Form.Item>
              )}
            </>
          )}
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            'data-testid': 'back-button',
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.CONTACT_INFORMATION,
                application,
                stepConfig,
              );

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
            'data-testid': 'next-button',
          }}
        />
      </Form>
    </>
  );
}

// Form options
const phoneTypeOptions = [
  {
    title: 'Cell',
    value: 'Cell',
  },
  {
    title: 'Home',
    value: 'Home',
  },
  {
    title: 'Work',
    value: 'Work',
  },
];
